import { CheckCircle2, Lock } from "lucide-react";
import type { RuleRequirementProgress } from "@/lib/rule-engine";
import { cn } from "@/lib/utils";

/** One requirement: check when met, lock + progress bar when countable.
 * Shared by the offer detail panel and the shop's locked-item requirements so
 * "how far am I" reads identically everywhere on the card. */
export function RequirementProgressRow({
  requirement,
}: {
  requirement: RuleRequirementProgress;
}) {
  const hasCount =
    typeof requirement.current === "number" &&
    typeof requirement.target === "number" &&
    requirement.target > 0;
  const ratio = hasCount
    ? Math.min(
        (requirement.current as number) / (requirement.target as number),
        1,
      )
    : requirement.met
      ? 1
      : 0;

  return (
    <div
      className={cn(
        "rounded-xl border p-3",
        requirement.met
          ? "border-border/50 bg-surface/60"
          : "border-border bg-surface",
      )}
    >
      <div className="flex items-center gap-2.5">
        {requirement.met ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-brand" />
        ) : (
          <Lock className="h-4 w-4 shrink-0 text-text-tertiary" />
        )}
        <p
          className={cn(
            "min-w-0 flex-1 text-sm leading-snug",
            requirement.met
              ? "text-text-secondary"
              : "font-medium text-text-primary",
          )}
        >
          {requirement.label}
        </p>
        {hasCount && (
          <span
            className={cn(
              "shrink-0 text-xs tabular-nums",
              requirement.met ? "text-text-tertiary" : "text-text-secondary",
            )}
          >
            {requirement.current} of {requirement.target}
          </span>
        )}
      </div>
      {hasCount && (
        <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-surface-alt">
          <div
            className={cn(
              "h-full rounded-full transition-[width] duration-500",
              requirement.met ? "bg-brand" : "bg-brand/80",
            )}
            style={{ width: `${Math.round(ratio * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}
